import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LipSyncMorphing from "./LipSyncMorphing";
import TrialEndScreen from "./components/TrialEndScreen";
import { getAuthToken } from "./api";
import toast from 'react-hot-toast';
import useBrowserSTT from './hooks/useBrowserSTT';
import useInterviewRecorder from './hooks/useInterviewRecorder';
import useProctoringSignals from './hooks/useProctoringSignals';
import { stopMediaStream } from './utils/mediaRecorder';

export default function RealtimeConversation({
  apiBase = "http://localhost:8000",
  interviewConfig = {},
  onGoBack,
  user,
  sessionId,
  onInterviewStarted,
  onInterviewComplete,
  onInterviewStopped,
  onLogout,
  skipWelcomePopup = false,
  autoStart = false
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Initializing...");
  const [conversation, setConversation] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(() => !skipWelcomePopup);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [interviewSessionId, setInterviewSessionId] = useState("");
  const [waitingForUser, setWaitingForUser] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [silenceCountdown, setSilenceCountdown] = useState(0);
  const [lastUserAnswer, setLastUserAnswer] = useState("");
  const [interviewInterrupted, setInterviewInterrupted] = useState(false);  // Track if interrupted
  const [showInterruptionButtons, setShowInterruptionButtons] = useState(false);  // Show download/back buttons after interruption message
  const [isTrial, setIsTrial] = useState(false);  // Free trial mode (2 questions)
  const [showTrialModal, setShowTrialModal] = useState(false);  // Show upgrade paywall
  const [trialPreviewScores, setTrialPreviewScores] = useState(null);  // Teaser scores
  const [completionWindow, setCompletionWindow] = useState(null);
  const [previewMediaStream, setPreviewMediaStream] = useState(null);
  const [previewError, setPreviewError] = useState("");

  const audioRef = useRef(null);
  const instructionAudioRef = useRef(null);
  const selfViewVideoRef = useRef(null);
  const rafRef = useRef(null);
  const questionAudiosRef = useRef([]);
  const silenceTimeoutRef = useRef(null);
  const isPlayingRef = useRef(false);
  const userResponseTimeoutRef = useRef(null);
  const hasSubmittedRef = useRef(false);
  const noSpeechTimerRef = useRef(null);
  const latestTranscriptRef = useRef('');
  const silenceCallbackRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const sessionIdRef = useRef("");  // Store session ID in ref to avoid stale closures
  const currentIndexRef = useRef(0);  // Store current index in ref to avoid stale closures
  const totalQuestionsRef = useRef(10);  // Store total questions in ref to avoid stale closures
  const messagesEndRef = useRef(null);  // For auto-scroll
  const isStoppedRef = useRef(false);  // Flag to prevent further processing after stop
  const abortControllerRef = useRef(null);  // AbortController for cancelling pending API requests
  const autoStartedRef = useRef(false);
  const latestTokenRef = useRef(null);  // Kept in sync so the pagehide handler has a token ready synchronously
  const stopSignalSentRef = useRef(false);  // Guards against sending /stop_interview twice
  const recordingEnabled = Boolean(interviewConfig?.invite_interview_id && interviewConfig?.job_id);
  const isInviteLinkedInterview = Boolean(interviewConfig?.invite_interview_id);
  const previewOnlyEnabled = !recordingEnabled && Boolean(interviewStarted || interviewSessionId);

  const {
    status: recordingStatus,
    error: recordingError,
    uploadProgress: recordingUploadProgress,
    mediaStream: recordingMediaStream,
    startRecording,
    stopRecording,
    retryUpload,
    connectTTSAudio
  } = useInterviewRecorder({
    enabled: recordingEnabled,
    jobId: interviewConfig?.job_id,
    interviewId: interviewConfig?.invite_interview_id
  });

  const {
    warning: proctoringWarning,
    faceStatus,
    persistError: proctoringPersistError,
    integrityScore,
    integrityLevel,
    flush: flushProctoring
  } = useProctoringSignals({
    enabled: recordingEnabled && Boolean(interviewStarted || interviewSessionId || interviewConfig?.session_id),
    interviewId: interviewConfig?.invite_interview_id,
    mediaStream: recordingMediaStream
  });

  const selfViewMediaStream = recordingMediaStream || previewMediaStream;
  const showSelfView = recordingEnabled || previewOnlyEnabled;

  const setInterviewTotalQuestions = (count) => {
    const normalized = Number(count) || 10;
    totalQuestionsRef.current = normalized;
    setTotalQuestions(normalized);
  };

  useEffect(() => {
    if (!selfViewVideoRef.current) {
      return;
    }

    if (selfViewMediaStream) {
      selfViewVideoRef.current.srcObject = selfViewMediaStream;
      selfViewVideoRef.current.play?.().catch(() => {});
    } else {
      selfViewVideoRef.current.srcObject = null;
    }
  }, [selfViewMediaStream]);

  useEffect(() => {
    if (!previewOnlyEnabled) {
      setPreviewError("");
      setPreviewMediaStream((stream) => {
        stopMediaStream(stream);
        return null;
      });
      return undefined;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setPreviewError("Camera preview is not supported in this browser.");
      return undefined;
    }

    let cancelled = false;
    let localStream = null;

    const startPreview = async () => {
      try {
        setPreviewError("");
        localStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 320 },
            height: { ideal: 180 },
            frameRate: { ideal: 12, max: 15 }
          },
          audio: false
        });

        if (cancelled) {
          stopMediaStream(localStream);
          return;
        }

        setPreviewMediaStream(localStream);
      } catch (err) {
        console.warn("Unable to start camera preview:", err);
        setPreviewError("Camera preview unavailable");
        setPreviewMediaStream(null);
      }
    };

    startPreview();

    return () => {
      cancelled = true;
      stopMediaStream(localStream);
      setPreviewMediaStream(null);
    };
  }, [previewOnlyEnabled]);

  // ---- Speech recognition hook (browser Web Speech API — en-IN for Indian English) ----
  const {
    transcript: sttTranscript,
    liveText: sttLiveText,
    isListening,
    startListening: sttStart,
    stopListening: sttStop,
    resetTranscript,
  } = useBrowserSTT({
    maxSilenceMs:     4000,
    onSilenceTimeout: (text) => silenceCallbackRef.current?.(text),
    onError:          (msg)  => setStatus(`⚠️ ${msg}`),
  });

  // Keep latestTranscriptRef in sync (interimTextRef no longer needed — transcript IS the live text)
  const interimTextRef = useRef('');
  useEffect(() => {
    latestTranscriptRef.current = sttTranscript;
    interimTextRef.current      = '';   // whisper returns finals only; no separate interim
  }, [sttTranscript]);

  // Derive userSpeaking from live transcript updates
  useEffect(() => {
    if (sttLiveText) {
      setUserSpeaking(true);
      lastActivityRef.current = Date.now();
    } else {
      const timer = setTimeout(() => setUserSpeaking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [sttLiveText]);

  // Update lastActivityRef when final transcript arrives
  useEffect(() => {
    if (sttTranscript) {
      lastActivityRef.current = Date.now();
    }
  }, [sttTranscript]);

  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation, sttTranscript, sttLiveText]);

  // ---------------- INSTRUCTION AUDIO ----------------
  useEffect(() => {
    if (showWelcomePopup) {
      const audio = new Audio(`${apiBase}/static/instruction_audio.mp3`);
      audio.preload = 'auto';
      connectTTSAudio(audio);
      instructionAudioRef.current = audio;

      // Desktop: autoplay works. Mobile: blocked by policy — will retry on OK click.
      audio.play().catch(() => {
        console.log('Instruction audio autoplay blocked (mobile). Will play on OK click.');
      });

      return () => {
        if (instructionAudioRef.current) {
          instructionAudioRef.current.pause();
          instructionAudioRef.current = null;
        }
      };
    }
  }, [showWelcomePopup, apiBase]);

  // ---------------- CLEANUP ----------------
  const cleanupAudio = async () => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {}
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
    if (userResponseTimeoutRef.current) clearTimeout(userResponseTimeoutRef.current);
    sttStop();
    flushProctoring();
    stopRecording();
    if (noSpeechTimerRef.current) {
      clearInterval(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
    
    // End interview session on backend
    try {
      const sessionId = sessionIdRef.current || sessionStorage.getItem('mockbot_session_id');
      if (sessionId && !stopSignalSentRef.current) {
        // Check if we still have auth before making the request
        // (prevents duplicate call during logout after App.jsx already handled it)
        try {
          const idToken = await getAuthToken();
          if (!idToken) {
            console.log("⚠️ No auth token - skipping stop_interview (likely already handled)");
            return;
          }

          console.log("🛑 Stopping interview session:", sessionId);
          stopSignalSentRef.current = true;
          await fetch(`${apiBase}/stop_interview`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`
            },
            body: JSON.stringify({ session_id: sessionId })
          });
        } catch (authError) {
          console.log("⚠️ No auth available - skipping stop_interview (likely already handled)");
        }
      }
    } catch (error) {
      console.error("Error stopping interview:", error);
    }
    
    if (!interviewConfig?.invite_interview_id) {
      try {
        const sid = sessionIdRef.current || sessionStorage.getItem('mockbot_session_id');
        const token = await getAuthToken();
        if (sid && token) {
          await fetch(`${apiBase}/interview/session/${encodeURIComponent(sid)}/cleanup`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
          });
        }
      } catch {}
    }
  };

  useEffect(() => {
    return () => cleanupAudio();
  }, []);

  // Keep a synchronously-readable auth token so the pagehide handler below never has to
  // await getAuthToken() -- a tab closing/navigating away can tear down the JS context
  // before an in-flight async call resolves.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const refreshToken = async () => {
      try {
        const token = await getAuthToken();
        if (!cancelled) latestTokenRef.current = token;
      } catch {}
    };
    refreshToken();
    const intervalId = setInterval(refreshToken, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user]);

  // Reliable fallback for candidates who close the tab, navigate away, or lose network
  // instead of reaching the normal "interview complete" screen: a plain fetch() made from
  // cleanupAudio()'s unmount cleanup is frequently cancelled by the browser mid-flight
  // during page unload, so those interviews were silently left at status=STARTED/IN_PROGRESS
  // forever with evaluation never triggered. `pagehide` + `fetch(..., {keepalive: true})` is
  // the standard reliable mechanism for firing a request during unload.
  useEffect(() => {
    const handlePageHide = () => {
      if (stopSignalSentRef.current) return;
      const sessionId = sessionIdRef.current || sessionStorage.getItem('mockbot_session_id');
      const token = latestTokenRef.current;
      if (!sessionId || !token) return;

      stopSignalSentRef.current = true;
      try {
        fetch(`${apiBase}/stop_interview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ session_id: sessionId }),
          keepalive: true
        }).catch(() => {});
      } catch {}
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [apiBase]);

  // The recording upload (uploadBlob in useInterviewRecorder) only starts once
  // MediaRecorder's onstop fires, well after stopRecording() is called, and it sends
  // the whole video as a plain fetch (no keepalive -- video blobs are far past the
  // ~64KB keepalive body cap). If the candidate closes the tab or navigates away while
  // that upload is still in flight, the browser cancels it and the recording is lost
  // forever ("Pending" on the recruiter dashboard). Warn on unload while it's running.
  useEffect(() => {
    if (!recordingEnabled) return;
    const handleBeforeUnload = (e) => {
      if (recordingStatus === "stopping" || recordingStatus === "uploading") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [recordingEnabled, recordingStatus]);

  // Check microphone permissions on mount
  useEffect(() => {
    const checkMicrophonePermission = async () => {
      try {
        console.log("🎤 Checking microphone permissions...");
        
        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.warn("⚠️ getUserMedia not supported");
          return;
        }
        
        // Try to get microphone access
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log("✅ Microphone access granted!");
        
        // Stop the stream immediately (we just needed to check permission)
        stream.getTracks().forEach(track => track.stop());
      } catch (error) {
        console.error("❌ Microphone permission error:", error);
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          toast.error('Microphone access is required for this interview. Please allow microphone access in your browser settings and refresh the page.');
        }
      }
    };
    
    checkMicrophonePermission();
  }, []);

  // ---------------- STREAMING INTERVIEW: GET GREETING ----------------
  const startInterview = async () => {
    setLoading(true);
    setStatus("Getting greeting...");
    
    try {
      // First, check if greeting data is already provided (from SetupScreen polling)
      if (interviewConfig.greeting && interviewConfig.session_id) {
        console.log("✅ Using pre-polled greeting data");
        console.log("📋 Session ID:", interviewConfig.session_id);
        
        setInterviewSessionId(interviewConfig.session_id);
        sessionIdRef.current = interviewConfig.session_id;  // Store in ref
        sessionStorage.setItem('mockbot_session_id', interviewConfig.session_id);  // Persist for report download
        onInterviewStarted?.({ sessionId: interviewConfig.session_id });
        startRecording();
        setInterviewTotalQuestions(interviewConfig.total_questions || 10);
        setIsTrial(!!interviewConfig.is_trial);
        
        setLoading(false);
        setStatus("Playing greeting...");
        
        // Play greeting immediately - pass session_id directly
        playGreeting(interviewConfig.greeting, interviewConfig.session_id);
        return;
      }

      // Legacy: check if we have pre-generated data in sessionStorage
      const preparedData = sessionStorage.getItem('preparedInterview');
      
      if (preparedData) {
        const data = JSON.parse(preparedData);
        const age = Date.now() - data.timestamp;
        
        // Use cached data if it's less than 5 minutes old
        if (age < 5 * 60 * 1000) {
          console.log("✅ Using pre-generated interview data from cache");
          console.log("📋 Session ID:", data.session_id);
          
          setInterviewSessionId(data.session_id);
          sessionIdRef.current = data.session_id;  // Store in ref
          sessionStorage.setItem('mockbot_session_id', data.session_id);  // Persist for report download
          onInterviewStarted?.({ sessionId: data.session_id });
          startRecording();
          setInterviewTotalQuestions(data.total_questions);
          setIsTrial(!!data.is_trial);
          
          setLoading(false);
          setStatus("Playing greeting...");
          
          // Clear the cache after use
          sessionStorage.removeItem('preparedInterview');
          
          // Play greeting immediately - pass session_id directly
          playGreeting(data.greeting, data.session_id);
          return;
        } else {
          console.log("⚠️ Cached data too old, regenerating...");
          sessionStorage.removeItem('preparedInterview');
        }
      }
      
      // Fallback: Generate on-demand if no cache available
      console.log("⚠️ No cached data, generating on-demand...");
      const authToken = user ? await getAuthToken() : null;
      
      // Build FormData for /get_greeting endpoint
      const formData = new FormData();
      formData.append("username", interviewConfig.candidate_name || user?.displayName || user?.email?.split('@')[0] || "Candidate");
      formData.append("role", interviewConfig.role);
      formData.append("years_experience", interviewConfig.years_experience);
      formData.append("difficulty", interviewConfig.difficulty);
      if (interviewConfig.mobile_number) {
        formData.append("mobile_number", interviewConfig.mobile_number);
      }
      if (interviewConfig.job_id) {
        formData.append("job_id", interviewConfig.job_id);
      }
      if (interviewConfig.b2b_interview_mode) {
        formData.append("b2b_interview_mode", interviewConfig.b2b_interview_mode);
      }
      if (interviewConfig.selected_company) {
        formData.append("target_company", interviewConfig.selected_company);
      }
      if (interviewConfig.interview_category) {
        formData.append("interview_category", interviewConfig.interview_category);
      }
      if (interviewConfig.uploaded_questions?.length) {
        formData.append("uploaded_questions", JSON.stringify(interviewConfig.uploaded_questions));
      }
      if (interviewConfig.resume_file) {
        formData.append("resume", interviewConfig.resume_file);
      }

      const headers = {};
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      const res = await fetch(`${apiBase}/get_greeting`, {
        method: "POST",
        headers,
        body: formData,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      console.log("✅ Interview data received:", data);
      console.log("📋 Session ID:", data.session_id);

      setInterviewSessionId(data.session_id);
      sessionIdRef.current = data.session_id;  // Store in ref
      sessionStorage.setItem('mockbot_session_id', data.session_id);  // Persist for report download
      onInterviewStarted?.({ sessionId: data.session_id });
      startRecording();
      setInterviewTotalQuestions(data.total_questions);
      setIsTrial(!!data.is_trial);
      
      setLoading(false);
      setStatus("Playing greeting...");
      
      // Play greeting immediately - pass session_id directly
      playGreeting(data.greeting, data.session_id);
      
    } catch (err) {
      console.error("Interview setup failed:", err);
      setStatus("Failed to start interview.");
      setLoading(false);
    }
  };

  // ---------------- GET NEXT QUESTION ----------------
  const getNextQuestionWithSession = async (index, userAnswer = null, sessionId = null) => {
    // Check if interview was stopped
    if (isStoppedRef.current) {
      console.log("⏹️ Interview stopped, skipping question fetch");
      return;
    }
    
    // Use provided sessionId or fall back to ref (always current)
    const activeSessionId = sessionId || sessionIdRef.current || interviewSessionId;
    
    console.log(`🔍 getNextQuestionWithSession called:`, {
      index,
      providedSessionId: sessionId,
      refSessionId: sessionIdRef.current,
      stateSessionId: interviewSessionId,
      activeSessionId
    });
    
    const questionLimit = totalQuestionsRef.current || totalQuestions;

    if (index > questionLimit) {
      setCurrentIndex(questionLimit);
      currentIndexRef.current = questionLimit;
      setStatus("Interview complete! Generating final message...");
      getCompletionMessage(isTrial, userAnswer || null);
      return;
    }

    if (!activeSessionId) {
      console.error("❌ No session ID available!");
      console.error("   State session ID:", interviewSessionId);
      console.error("   Ref session ID:", sessionIdRef.current);
      console.error("   Provided session ID:", sessionId);
      setStatus("Error: Session not initialized. Please restart.");
      return;
    }

    setStatus(`Generating question ${index}...`);
    const safeConversation = conversation.map((message) => ({
      ...message,
      text: typeof message?.text === "string" ? message.text : ""
    }));
    if (userAnswer != null && typeof userAnswer !== "string") {
      userAnswer = String(userAnswer);
    }
    
    try {
      const authToken = user ? await getAuthToken() : null;
      const headers = { "Content-Type": "application/json" };
      if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

      console.log(`📡 Requesting question ${index} with session: ${activeSessionId}`);
      console.log("User answer context:", userAnswer ? `"${userAnswer.slice(0, 50)}..."` : "No context");
      console.log(`Full conversation history (${safeConversation.length} messages):`, safeConversation.map(m => `${m.speaker || "unknown"}: ${m.text.slice(0, 30)}...`));

      // Create new AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      const res = await fetch(`${apiBase}/get_question`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          session_id: activeSessionId,
          question_index: index,
          user_answer: userAnswer,  // Send previous answer for context
          conversation_history: safeConversation  // Send full conversation history
        }),
        signal: controller.signal  // Attach abort signal
      });

      // Check if request was aborted (stop button pressed)
      if (isStoppedRef.current) {
        console.log("⏹️ Request aborted: Interview stopped");
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // ============ TRIAL ENDED CHECK ============
      if (data.trial_ended) {
        console.log("🛑 Trial ended:", data.message);
        sttStop();
        setWaitingForUser(false);
        setInterviewStarted(false);
        // Store scores so they're ready before audio finishes
        if (data.preview_scores) setTrialPreviewScores(data.preview_scores);

        // 📊 Meta Pixel: track free trial completion
        if (typeof window.fbq === "function") {
          window.fbq("trackCustom", "FreeTrialCompleted", {
            communication_score: data.preview_scores?.communication || "N/A",
            confidence_score: data.preview_scores?.confidence || "N/A",
          });
        }

        // Play thank-you audio + lip sync, then show the modal
        getCompletionMessage(true);
        return;
      }

      if (data.error) {
        console.error("Error getting question:", data.error);
        setStatus(`Error loading question: ${data.error}`);
        return;
      }

      if (data.interview_complete) {
        const questionLimit = totalQuestionsRef.current || totalQuestions;
        setCurrentIndex(questionLimit);
        currentIndexRef.current = questionLimit;
        setStatus("Interview complete! Generating final message...");
        getCompletionMessage(isTrial, userAnswer || null);
        return;
      }

      console.log(`✅ Question ${index} generated:`, data.question.text);
      
      // Check if this is a repeated question
      if (data.question.is_repeat) {
        console.log(`🔁 This is a repeated question`);
      }

      // Play question immediately (no delay)
      playQuestion(data.question, index);
      
    } catch (err) {
      // Ignore AbortError (normal when stop is pressed)
      if (err.name === 'AbortError') {
        console.log("⏹️ Question fetch was cancelled");
        return;
      }
      console.error("Failed to get question:", err);
      setStatus("Failed to load question.");
    }
  };

  // Wrapper that uses state session ID
  const getNextQuestion = async (index, userAnswer = null) => {
    return getNextQuestionWithSession(index, userAnswer, null);
  };

  // ---------------- PLAY GREETING ----------------
  const playGreeting = (greeting, sessionId) => {
    console.log("🎬 Playing greeting:", greeting);
    console.log("📋 Using session ID:", sessionId);
    
    // ============ HANDLE ASYNC GREETING GENERATION ============
    // If greeting is still being generated (PROCESSING status),
    // poll the session endpoint until it's READY
    if (greeting.status === "PROCESSING") {
      console.log("⏳ Greeting still being generated, polling for completion...");
      setStatus("Generating greeting (this may take 10-15 seconds)...");
      
      // Poll every 1 second
      const pollInterval = setInterval(async () => {
        try {
          const authToken = user ? await getAuthToken() : null;
          const res = await fetch(`${apiBase}/interview/session/${sessionId}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const sessionData = await res.json();
          
          const greetingStatus = sessionData.greeting?.status;
          console.log("🔍 Greeting status:", greetingStatus);
          
          if (greetingStatus === "READY") {
            clearInterval(pollInterval);
            // Greeting is ready, play it
            const readyGreeting = {
              ...greeting,
              status: "READY",
              audio_url: sessionData.greeting.audio_url,
              duration: sessionData.greeting.duration,
              timeline: sessionData.greeting.timeline || greeting.timeline || []
            };
            playGreeting(readyGreeting, sessionId);
          } else if (greetingStatus === "FAILED") {
            clearInterval(pollInterval);
            const errorMsg = sessionData.greeting?.error || "Unknown error";
            console.error("❌ Greeting generation failed:", errorMsg);
            setStatus(`Greeting generation failed: ${errorMsg}`);
          }
        } catch (err) {
          clearInterval(pollInterval);
          console.error("Error polling greeting status:", err);
          setStatus("Error generating greeting.");
        }
      }, 1000);
      
      return;
    }
    
    // ============ GREETING IS READY - PLAY AUDIO ============
    if (greeting.status !== "READY") {
      console.warn("⚠️ Greeting not ready:", greeting.status);
      setStatus(`Greeting not ready: ${greeting.status || "UNKNOWN"}`);
      return;
    }
    
    // Use HTTP URL provided by backend (e.g., "/audio/greeting_*.wav")
    const audioUrl = greeting.audio_url;
    if (!audioUrl) {
      console.error("❌ No audio URL provided by backend");
      setStatus("Missing audio URL from backend.");
      return;
    }
    
    // Prepend API base URL to create full HTTP URL
    // Example: apiBase = "http://localhost:8000", audioUrl = "/audio/greeting_*.wav"
    // Result: "http://localhost:8000/audio/greeting_*.wav"
    const fullAudioUrl = audioUrl.startsWith("http") ? audioUrl : `${apiBase}${audioUrl}`;
    
    console.log("🌐 Full audio URL:", fullAudioUrl);
    console.log("📊 Duration:", greeting.duration);

    // Add greeting to conversation IMMEDIATELY before playing
    setConversation([{ speaker: "assistant", text: greeting.text || "" }]);
    
    isPlayingRef.current = true;
    setTtsPlaying(true);
    setStatus("Playing greeting...");
    setTimeline(greeting.timeline || []);

    // ============ CREATE AUDIO ELEMENT ============
    const audio = new Audio();
    audio.src = fullAudioUrl;
    connectTTSAudio(audio);
    audioRef.current = audio;

    audio.onended = () => {
      isPlayingRef.current = false;
      setTtsPlaying(false);
      setTimeline([]);

      // Check if interview was stopped during greeting
      if (isStoppedRef.current) {
        console.log("⏹️ Interview stopped during greeting, not starting questions");
        return;
      }

      setInterviewStarted(true);
      
      
      // Move to first question after greeting
      setStatus("Starting first question...");
      setCurrentIndex(1);
      currentIndexRef.current = 1;  // Update ref as well
      
      console.log("✅ Greeting complete! Moving to question 1...");
      console.log("📋 Using session:", sessionId);
      
      // Get first question - pass session ID explicitly
      getNextQuestionWithSession(1, null, sessionId);
    };

    audio.onerror = (e) => {
      console.error("❌ Audio playback error:", {
        code: audio.error?.code,
        message: audio.error?.message,
        url: fullAudioUrl
      });
      
      // Map error codes to readable messages
      const errorMessages = {
        1: "MEDIA_ERR_ABORTED - Loading was aborted",
        2: "MEDIA_ERR_NETWORK - Network error occurred",
        3: "MEDIA_ERR_DECODE - Decoding failed (check file format)",
        4: "MEDIA_ERR_SRC_NOT_SUPPORTED - Audio format not supported"
      };
      
      const errorMsg = errorMessages[audio.error?.code] || `Error code ${audio.error?.code}`;
      console.error("Audio error details:", errorMsg);
      
      setTtsPlaying(false);
      isPlayingRef.current = false;
      setStatus(`Failed to play greeting: ${errorMsg}`);
    };

    audio.load();
    audio.play().catch(err => {
      console.error("❌ Audio play() promise rejected:", err);
      setTtsPlaying(false);
      isPlayingRef.current = false;
      setStatus(`Failed to play greeting: ${err.message}`);
    });
  };

  // ---------------- PLAY QUESTION ----------------
  const playQuestion = (question, index) => {
    // Check if interview was stopped
    if (isStoppedRef.current) {
      console.log("⏹️ Interview stopped, skipping question playback");
      return;
    }
    
    console.log(`🎬 Playing question ${index}:`, question);
    console.log("📊 Timeline:", question.timeline);
    console.log("⏱️ Duration:", question.duration);
    
    isPlayingRef.current = true;
    setTtsPlaying(true);
    setStatus(`Playing question ${index}...`);
    setTimeline(question.timeline || []);

    const audio = new Audio(`data:audio/wav;base64,${question.audio_b64}`);
    connectTTSAudio(audio);
    audioRef.current = audio;

    audio.onplay = () => {
      setConversation((prev) => [...prev, { speaker: "assistant", text: question.text || "" }]);
    };

    audio.onended = () => {
      isPlayingRef.current = false;
      setTtsPlaying(false);
      setTimeline([]);

      
      setWaitingForUser(true);
      const questionLimit = totalQuestionsRef.current || totalQuestions;
      setStatus(`🎤 Your turn - Question ${Math.min(index, questionLimit)}/${questionLimit}`);
      
      // Start listening for user response immediately
      startListeningForResponse(index);
    };

    audio.onerror = (e) => {
      console.error("Audio error:", e);
      setTtsPlaying(false);
      isPlayingRef.current = false;
    };

    audio.load();
    audio.play();
  };

  // Check if user is asking to repeat the question HANDLE repeat 

  const handleUserResponse = (text) => {
    if (isStoppedRef.current) return;
    if (!text || text.trim().length < 2) {
      console.log("⚠️ handleUserResponse: No valid text provided");
      return;
    }
    
    const t = text.trim();
    console.log("✅ handleUserResponse called with:", t);
    console.log("   waitingForUser:", waitingForUser);
    console.log("   currentIndex:", currentIndex);
    
    // Normal answer flow
    setConversation(p => [...p, { speaker: 'user', text: t }]);
    setLastUserAnswer(t);
    
    // Clear all timeouts and stop recognition
    if (userResponseTimeoutRef.current) {
      clearTimeout(userResponseTimeoutRef.current);
      userResponseTimeoutRef.current = null;
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    sttStop();
    if (noSpeechTimerRef.current) {
      clearInterval(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
    
    setWaitingForUser(false);
    setUserSpeaking(false);
    setSilenceCountdown(0);
    
    console.log("📊 State updated, moving to next question...");
    console.log(`📍 Current state - currentIndex: ${currentIndex}, currentIndexRef: ${currentIndexRef.current}`);
    
    // Calculate next index
    const nextIndex = currentIndexRef.current + 1;
    const questionLimit = totalQuestionsRef.current || totalQuestions;
    console.log(`🔢 Calculated nextIndex: ${nextIndex}, totalQuestions: ${questionLimit}`);
    
    // Check if interview is complete
    if (nextIndex > questionLimit) {
      setCurrentIndex(questionLimit);
      currentIndexRef.current = questionLimit;
      console.log("🎉 Interview complete! Getting completion message...");
      setStatus("🎉 Interview complete! Generating final message...");
      getCompletionMessage(isTrial, t);
      return;
    }

    // Continue to next question
    setStatus("Generating next question based on your answer...");
    console.log(`✅ Proceeding to question ${nextIndex}...`);
    setCurrentIndex(nextIndex);
    currentIndexRef.current = nextIndex;
    getNextQuestion(nextIndex, t);
  };



  // Fetch trial preview scores from backend (used after trial completion audio)
  const fetchTrialPreviewScores = async () => {
    try {
      const sid = sessionIdRef.current || sessionStorage.getItem('mockbot_session_id');
      const authToken = user ? await getAuthToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${apiBase}/get_question`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: sid, question_index: 999, user_answer: '', conversation_history: [] })
      });
      if (!res.ok) return null;
      const data = await res.json();
      return (data.trial_ended && data.preview_scores) ? data.preview_scores : null;
    } catch { return null; }
  };

  const completionText = interviewConfig?.invite_interview_id
    ? "Congratulations on completing the interview! Thank you for your time and effort. Your responses have been recorded."
    : "Congratulations on completing the interview! Thank you for your time and effort. Your responses have been recorded and your performance evaluation is being generated. We hope this practice helps you prepare for your upcoming interviews. Good luck!";

  const interruptionText = "Your interview was ended. Thank you for participating. Your responses have been recorded.";

  const sanitizeClosingText = (text, fallbackText = completionText) => {
    const cleanText = typeof text === "string" && text.trim() ? text.trim() : fallbackText;
    return cleanText
      .replace(/You can download your detailed report from the dashboard\.\s*/gi, "")
      .replace(/You can download the report now\.\s*/gi, "")
      .replace(/You can download your report now\.\s*/gi, "")
      .replace(/You can review your answers and download your report\.\s*/gi, "")
      .trim();
  };

  const openCompletionWindow = (title, message, type = "completed") => {
    setCompletionWindow({
      title,
      message: sanitizeClosingText(message),
      type
    });
  };

  // Add new function to get completion message (after handleUserResponse)

  const getCompletionMessage = async (isTrialSession = false, finalAnswer = null) => {
    console.log("🎊 Interview completed! Ending interview and getting completion message...");

    // Start fetching trial scores immediately (parallel with audio load)
    const scoresPromise = isTrialSession ? fetchTrialPreviewScores() : Promise.resolve(null);

    try {
      const sessionId = sessionIdRef.current || sessionStorage.getItem('mockbot_session_id');
      const authToken = user ? await getAuthToken() : null;
      flushProctoring();
      stopRecording();

      // Call end_interview to trigger background evaluation (non-trial only)
      if (sessionId && !isTrialSession) {
        console.log("📤 Calling /end_interview for completed interview...");

        // finalAnswer is passed directly from the caller to avoid stale React state.
        // lastUserAnswer state may hold the PREVIOUS question's answer because
        // setLastUserAnswer(t) is async and hasn't flushed before this function runs.
        const answerToStore = finalAnswer || lastUserAnswer;

        // Build conversation history; React state may not yet include the final answer,
        // so append it manually when finalAnswer is provided.
        const conversationWithFinal = [...conversation];
        if (finalAnswer) {
          const last = conversationWithFinal[conversationWithFinal.length - 1];
          if (!last || last.speaker !== "user" || last.text !== finalAnswer) {
            conversationWithFinal.push({ speaker: "user", text: finalAnswer });
          }
        }

        const formData = new FormData();
        formData.append("session_id", sessionId);
        if (answerToStore) {
          formData.append("last_user_answer", answerToStore);
        }
        // Send full conversation so backend can fall back to it if Firestore messages are incomplete
        formData.append("conversation_history", JSON.stringify(
          conversationWithFinal.map(m => ({ speaker: m.speaker || "", text: m.text || "" }))
        ));

        const endRes = await fetch(`${apiBase}/end_interview`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${authToken}`
          },
          body: formData
        });

        if (endRes.ok) {
          const endData = await endRes.json();
          console.log("✅ Interview ended, evaluation scheduled:", endData.evaluation_status);
          onInterviewComplete?.({ sessionId });
        } else {
          console.error("⚠️ Failed to end interview:", endRes.status);
        }
      }

      // Priority 1 — dedicated trial completion audio (best text + lip sync)
      if (isTrialSession) {
        try {
          const trialRes = await fetch(`${apiBase}/static/trial_completion_phonemes_cache.json`);
          if (trialRes.ok) {
            const trialData = await trialRes.json();
            if (trialData.audio_url || trialData.audio_b64) {
              console.log("✅ Loaded trial completion message");
              playCompletionMessage(trialData, true, scoresPromise);
              return;
            }
          }
        } catch (_) {
          console.log("⚠️ No trial completion cache, trying generic completion cache");
        }
      }

      // Priority 2 — generic completion cache
      try {
        const cacheRes = await fetch(`${apiBase}/static/completion_phonemes_cache.json`);
        if (cacheRes.ok) {
          const cacheData = await cacheRes.json();
          cacheData.text = sanitizeClosingText(cacheData.text, completionText);
          if (interviewConfig?.invite_interview_id) {
            cacheData.text = completionText;
          }
          cacheData.audio_url = null;
          cacheData.audio_b64 = null;
          if (cacheData.audio_url || cacheData.audio_b64) {
            console.log("✅ Loaded static completion message");
            playCompletionMessage(cacheData, isTrialSession, scoresPromise);
            return;
          }
          playCompletionMessage(cacheData, isTrialSession, scoresPromise);
          return;
        }
      } catch (cacheErr) {
        console.log("⚠️ No static completion cache, trying interruption audio as fallback");
      }

      // Fallback: use interruption audio (has real lip-sync data) to play thank-you, then show modal/end
      try {
        const intRes = await fetch(`${apiBase}/static/interruption_phonemes_cache.json`);
        if (intRes.ok) {
          const intData = await intRes.json();
          if (intData.audio_url || intData.audio_b64) {
            console.log("✅ Using interruption audio as completion fallback");
            const completionData = {
              text: isTrialSession
                ? "Great effort on your trial interview! You've completed 2 questions. Here's a preview of your performance."
                : completionText,
              timeline: intData.timeline || [],
              audio_url: null,
              audio_b64: null,
            };
            playCompletionMessage(completionData, isTrialSession, scoresPromise);
            return;
          }
        }
      } catch (intErr) {
        console.log("⚠️ No interruption cache either, showing text-only fallback");
      }

      // Last resort: no audio
      const fallbackCompletionText = isTrialSession
        ? "Great effort! Here's a preview of your trial interview performance."
        : "Thank you for completing the interview! Your responses have been recorded.";
      const cleanFallbackCompletionText = sanitizeClosingText(fallbackCompletionText);
      setConversation((prev) => [...prev, { speaker: "assistant", text: cleanFallbackCompletionText }]);

      if (isTrialSession) {
        const scores = await scoresPromise.catch(() => null);
        if (scores) setTrialPreviewScores(scores);
        setStatus("Free trial completed");
        setShowTrialModal(true);
      } else {
        setStatus("✅ Interview complete! Your evaluation is being generated.");
        setInterviewStarted(false);
        openCompletionWindow("Interview Completed", cleanFallbackCompletionText);
        onInterviewComplete?.({ sessionId: sessionIdRef.current || sessionStorage.getItem('mockbot_session_id') });
      }

    } catch (err) {
      console.error("Failed to end interview:", err);
      if (isTrialSession) {
        setStatus("Free trial completed");
        setShowTrialModal(true);
      } else {
        setStatus("✅ Interview complete! Thank you for participating.");
        setInterviewStarted(false);
        openCompletionWindow("Interview Completed", completionText);
        onInterviewComplete?.({ sessionId: sessionIdRef.current || sessionStorage.getItem('mockbot_session_id') });
      }
    }
  };

  // Add new function to get interruption message (after getCompletionMessage)

  // onPlay callback fires as soon as audio begins; onComplete fires when audio ends
  const getInterruptionMessage = async (onPlay = null, onComplete = null) => {
    try {
      const cacheRes = await fetch(`${apiBase}/static/interruption_phonemes_cache.json`);
      if (cacheRes.ok) {
        const cacheData = await cacheRes.json();
        playPrerecordedInterruptionWithPhonemes(cacheData, onPlay, onComplete);
      } else {
        if (onPlay) onPlay();
        if (onComplete) onComplete();
      }
    } catch (err) {
      console.error("Failed to load interruption:", err);
      if (onPlay) onPlay();
      if (onComplete) onComplete();
    }
  };

  const playPrerecordedInterruptionWithPhonemes = (cacheData, onPlay = null, onComplete = null) => {
    const text = sanitizeClosingText(cacheData.text, interruptionText);
    const timeline = [];

    setConversation((prev) => [...prev, { speaker: "assistant", text: text }]);
    isPlayingRef.current = true;
    setTtsPlaying(true);
    setStatus("Closing interview...");
    setTimeline(timeline || []);

    const _finish = () => {
      isPlayingRef.current = false;
      setTtsPlaying(false);
      setTimeline([]);
      if (onComplete) {
        onComplete();
      } else if (!onPlay) {
        // Default non-trial behaviour (no callbacks at all)
        setStatus("Interview ended. Thank you for participating.");
        setInterviewStarted(false);
        setShowInterruptionButtons(true);
        openCompletionWindow("Interview Ended", text, "interrupted");
      }
      // If onPlay was provided (trial case), the modal is already shown — just clean up
    };

    if (onPlay) onPlay();
    _finish();
  };



  const playCompletionMessage = (completion, isTrialSession = false, scoresPromise = null) => {
    // Check if interview was stopped
    if (isStoppedRef.current) {
      console.log("⏹️ Interview stopped, skipping completion message playback");
      return;
    }

    console.log("🎬 Playing completion message:", completion);
    completion.text = sanitizeClosingText(completion.text, completionText);

    isPlayingRef.current = true;
    setTtsPlaying(true);
    setStatus("Playing completion message...");
    setTimeline(completion.timeline || []);

    const _showTrialModal = (scores) => {
      setConversation((prev) => [...prev, { speaker: "assistant", text: completion.text }]);
      if (scores) setTrialPreviewScores(scores);
      setStatus("Free trial completed");
      setShowTrialModal(true);
    };

    const _finishNonTrial = () => {
      setConversation((prev) => [...prev, { speaker: "assistant", text: completion.text }]);
      setStatus(interviewConfig?.invite_interview_id
        ? "✅ Interview complete! Thank you for participating."
        : "✅ Interview complete! Your evaluation is being generated.");
      setInterviewStarted(false);
      openCompletionWindow("Interview Completed", completion.text);
      onInterviewComplete?.({ sessionId: sessionIdRef.current || sessionStorage.getItem('mockbot_session_id') });
    };

    // Handle both static cache format and API format
    let audioSrc;
    if (!isTrialSession) {
      completion.audio_b64 = null;
      completion.audio_url = null;
    }
    if (completion.audio_b64) {
      audioSrc = `data:audio/wav;base64,${completion.audio_b64}`;
    } else if (completion.audio_url) {
      audioSrc = `${apiBase}${completion.audio_url}`;
    } else {
      console.log("No completion audio file available; showing completion window.");
      if (isTrialSession) {
        setTtsPlaying(false);
        isPlayingRef.current = false;
        setTimeline([]);
        const p = scoresPromise || Promise.resolve(null);
        p.catch(() => null).then(_showTrialModal);
      } else {
        setTtsPlaying(false);
        isPlayingRef.current = false;
        setTimeline([]);
        _finishNonTrial();
      }
      return;
    }

    const audio = new Audio(audioSrc);
    connectTTSAudio(audio);
    audioRef.current = audio;

    audio.onplay = () => {
      // For trial: show the paywall immediately as audio starts (not after it ends)
      if (isTrialSession) {
        const p = scoresPromise || Promise.resolve(null);
        p.catch(() => null).then(_showTrialModal);
      }
    };

    audio.onended = () => {
      isPlayingRef.current = false;
      setTtsPlaying(false);
      setTimeline([]);
      console.log("🎉 Completion message played successfully!");
      // For non-trial: show completion state after audio finishes
      if (!isTrialSession) {
        _finishNonTrial();
      }
    };

    audio.onerror = (e) => {
      console.error("Audio error:", e);
      setTtsPlaying(false);
      isPlayingRef.current = false;
      setTimeline([]);
      if (isTrialSession) {
        const p = scoresPromise || Promise.resolve(null);
        p.catch(() => null).then(_showTrialModal);
      } else {
        _finishNonTrial();
      }
    };

    audio.load();
    audio.play();
  };

  // ---- Silence callback (needs handleUserResponse in scope) ----
  silenceCallbackRef.current = (currentText) => {
    if (hasSubmittedRef.current || isStoppedRef.current) return;
    hasSubmittedRef.current = true;
    if (noSpeechTimerRef.current) {
      clearInterval(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
    console.log(`⏱️ ✅ 8s silence → submitting: "${currentText}"`);
    handleUserResponse(currentText);
  };

  // ---------------- START LISTENING FOR RESPONSE ----------------
  const startListeningForResponse = (questionIndex) => {
    console.log("🎬 startListeningForResponse called for question", questionIndex);

    hasSubmittedRef.current = false;
    lastActivityRef.current = Date.now();
    resetTranscript();
    sttStart().catch((err) => {
      console.error("❌ STT startListening failed:", err);
      setStatus("⚠️ Microphone error: " + err.message);
    });
    setUserSpeaking(false);
    setSilenceCountdown(4);

    // Clear any existing no-speech timer
    if (noSpeechTimerRef.current) {
      clearInterval(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }

    const listenStartTime = Date.now();

    // No-speech skip timer + countdown display
    noSpeechTimerRef.current = setInterval(() => {
      if (hasSubmittedRef.current || isStoppedRef.current) {
        clearInterval(noSpeechTimerRef.current);
        noSpeechTimerRef.current = null;
        return;
      }

      // Use the current transcript (Whisper returns finals; no separate interim)
      const currentText = latestTranscriptRef.current.trim();

      // Update silence countdown display
      const silenceSec = (Date.now() - lastActivityRef.current) / 1000;
      const countdown = Math.max(0, 4 - Math.floor(silenceSec));
      setSilenceCountdown(countdown);

      console.log(`⏱️ Silence: ${silenceSec.toFixed(1)}s / 4s | Transcript: ${currentText.length} chars`);

      // 10s of total silence with no speech → skip question
      const totalElapsed = (Date.now() - listenStartTime) / 1000;
      if (totalElapsed >= 10 && !currentText) {
        console.log("⏱️ ⏭️ 10s total silence → skipping question");
        hasSubmittedRef.current = true;
        clearInterval(noSpeechTimerRef.current);
        noSpeechTimerRef.current = null;
        sttStop();
        handleUserResponse("No answer provided");
      }
    }, 200);
  };





  // ---------------- HANDLE WELCOME POPUP ----------------
  const handleWelcomeOk = async () => {
    const audio = instructionAudioRef.current;
    if (audio) {
      if (audio.paused) {
        // Mobile: autoplay was blocked — this button click IS a user gesture, so play now
        audio.play().catch(() => {});
      }
      // Stop instruction audio after a short delay (lets it play briefly on mobile)
      setTimeout(() => {
        audio.pause();
        instructionAudioRef.current = null;
      }, 3000);
    }

    setShowWelcomePopup(false);

    // Wait for popup close animation before starting interview
    setTimeout(async () => {
      await startInterview();
    }, 500);
  };

  useEffect(() => {
    if (!autoStart || showWelcomePopup || autoStartedRef.current) {
      return;
    }

    autoStartedRef.current = true;
    startInterview();
  }, [autoStart, showWelcomePopup]);

  // ---------------- UI ----------------
  return (
    <div className="container">
      {/* ---------------- TRIAL END SCREEN (PAYWALL) ---------------- */}
      {showTrialModal && (
        <TrialEndScreen
          previewScores={trialPreviewScores}
          onUpgrade={() => navigate("/payment?trialOffer=true")}
          onRetry={() => { setShowTrialModal(false); navigate("/setup"); }}
        />
      )}

      {completionWindow && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.78)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "1rem"
          }}
        >
          <div
            style={{
              width: "min(30rem, 100%)",
              background: "#ffffff",
              color: "#111827",
              borderRadius: "0.5rem",
              boxShadow: "0 1.5rem 4rem rgba(0,0,0,0.32)",
              padding: "1.75rem",
              textAlign: "center"
            }}
          >
            <h2 style={{ margin: "0 0 0.75rem", fontSize: "1.6rem" }}>
              {completionWindow.title}
            </h2>
            <p style={{ margin: "0 0 1.25rem", color: "#475569", lineHeight: 1.65 }}>
              {completionWindow.message}
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "center",
                flexWrap: "wrap"
              }}
            >
              {!isInviteLinkedInterview && (
                <button
                  type="button"
                  className="logout-button"
                  onClick={() => setCompletionWindow(null)}
                  style={{
                    background: "#f8fafc",
                    color: "#334155",
                    border: "1px solid #cbd5e1",
                    padding: "0.75rem 1.25rem",
                    borderRadius: "0.375rem",
                    cursor: "pointer",
                    fontWeight: 700
                  }}
                >
                  Review Transcript
                </button>
              )}
              {recordingEnabled && (recordingStatus === "stopping" || recordingStatus === "uploading") ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                  <div
                    aria-hidden="true"
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      border: "3px solid #cbd5e1",
                      borderTopColor: "#2563eb",
                      borderRadius: "50%",
                      animation: "vagmee-upload-spin 0.8s linear infinite"
                    }}
                  />
                  <style>{"@keyframes vagmee-upload-spin { to { transform: rotate(360deg); } }"}</style>
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#64748b", maxWidth: "20rem" }}>
                    Uploading your recording — please keep this window open.
                  </p>
                </div>
              ) : recordingEnabled && recordingStatus === "upload-failed" ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#b91c1c", maxWidth: "20rem" }}>
                    Recording upload failed{recordingError ? `: ${recordingError}` : "."}
                  </p>
                  <button
                    type="button"
                    className="logout-button"
                    onClick={retryUpload}
                    style={{
                      background: "#2563eb",
                      color: "#fff",
                      border: "none",
                      padding: "0.75rem 1.25rem",
                      borderRadius: "0.375rem",
                      cursor: "pointer",
                      fontWeight: 700
                    }}
                  >
                    Retry Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (isInviteLinkedInterview && onLogout) {
                        onLogout();
                        return;
                      }
                      if (onGoBack) {
                        onGoBack();
                        return;
                      }
                      navigate("/dashboard");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      textDecoration: "underline",
                      fontSize: "0.75rem",
                      color: "#94a3b8",
                      cursor: "pointer"
                    }}
                  >
                    Leave anyway
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="logout-button"
                  onClick={() => {
                    if (isInviteLinkedInterview && onLogout) {
                      onLogout();
                      return;
                    }
                    if (onGoBack) {
                      onGoBack();
                      return;
                    }
                    navigate("/dashboard");
                  }}
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    padding: "0.75rem 1.25rem",
                    borderRadius: "0.375rem",
                    cursor: "pointer",
                    fontWeight: 700
                  }}
                >
                  {isInviteLinkedInterview ? "Logout" : "Go to Dashboard"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---------------- WELCOME POPUP ---------------- */}
      {showWelcomePopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "2.5rem",
              borderRadius: "1.25rem",
              boxShadow: "0 1.25rem 3.75rem rgba(0,0,0,0.5)",
              maxWidth: "30rem",
              textAlign: "center",
              color: "white",
            }}
          >
            <h2 style={{ marginBottom: "1.25rem", fontSize: "1.75rem" }}>
              Interview Instructions
            </h2>
            <div style={{ 
              fontSize: "1rem", 
              lineHeight: "1.8", 
              marginBottom: "1.5625rem",
              textAlign: "left"
            }}>
              <ul style={{ 
                paddingLeft: "1.25rem", 
                margin: 0,
                listStyleType: "disc"
              }}>
                <li style={{ marginBottom: "0.625rem" }}>
                  This is a Mock Virtual Interview which is purely AI driven.
                </li>
                <li style={{ marginBottom: "0.625rem" }}>
                  Please speak <strong>loud and clear</strong>.
                </li>
                <li style={{ marginBottom: "0.625rem" }}>
                   5 seconds of continuous silence will be considered as end of answer.
                </li>
                <li style={{ marginBottom: "0.625rem" }}>
                  <strong>Note:</strong> Generally it takes 7 to 8 seconds to generate a new question based on your answer.
                </li>
              </ul>
            </div>
            <button
              onClick={handleWelcomeOk}
              disabled={loading}
              style={{
                padding: "0.875rem 2.8125rem",
                fontSize: "1.125rem",
                backgroundColor: loading ? "#ccc" : "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "3.125rem",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "bold",
                boxShadow: "0 0.25rem 0.9375rem rgba(0,0,0,0.3)",
                transition: "all 0.3s",
              }}
            >
              {loading ? "Preparing..." : "OK, Let's Start"}
            </button>
            {loading && (
              <p style={{ marginTop: "1.25rem", fontSize: "0.875rem", opacity: 0.8 }}>
                Generating questions, please wait...
              </p>
            )}
          </div>
        </div>
      )}

      {/* ---------------- LIP SYNC ---------------- */}
      <div className="lip-sync-container">
        {showSelfView && (
          <div className="candidate-self-view" aria-label="Camera preview">
            {selfViewMediaStream ? (
              <video
                ref={selfViewVideoRef}
                className="candidate-self-view-video"
                muted
                playsInline
                autoPlay
              />
            ) : (
              <div className="candidate-self-view-placeholder">
                {previewError || "Camera preview"}
              </div>
            )}
            <div className="candidate-self-view-label">
              You
            </div>
          </div>
        )}
        <LipSyncMorphing
          timeline={timeline || []}
          audioRef={audioRef}
          size={640}
          showDebug={false}
        />
         {/* Status and real-time indicators */}
        <div style={{ marginTop: "0.3rem", display: "flex", flexDirection: "column", gap: "0.1rem" ,alignItems: "center"}}>
          <p style={{ color: "#aaa", margin: 0 }}>{status}</p>
          {proctoringWarning && (
            <div style={{
              width: "min(42rem, 100%)",
              padding: "0.6rem 0.8rem",
              borderRadius: "0.375rem",
              background: "rgba(245, 158, 11, 0.18)",
              border: "1px solid rgba(245, 158, 11, 0.5)",
              color: "#fde68a",
              fontWeight: 700,
              textAlign: "center"
            }}>
              {proctoringWarning}
            </div>
          )}
          {recordingEnabled && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              color: recordingError ? "#fecaca" : "#bbf7d0",
              fontSize: "0.82rem",
              minHeight: "1.2rem"
            }}>
              <span style={{
                width: "0.55rem",
                height: "0.55rem",
                borderRadius: "999px",
                background: recordingStatus === "recording" ? "#ef4444" : "#64748b",
                display: "inline-block"
              }} />
              <span>
                {recordingStatus === "recording" && "Recording interview"}
                {recordingStatus === "requesting-permission" && "Requesting camera permission"}
                {recordingStatus === "stopping" && "Finalizing recording"}
                {recordingStatus === "uploading" && `Uploading recording ${recordingUploadProgress}%`}
                {recordingStatus === "uploaded" && "Recording saved"}
                {recordingStatus === "permission-denied" && "Recording permission denied"}
                {recordingStatus === "upload-failed" && "Recording upload failed"}
                {recordingStatus === "failed" && "Recording failed"}
                {recordingStatus === "unsupported" && "Recording unsupported"}
                {recordingStatus === "idle" && "Recording ready"}
              </span>
              {recordingStatus === "upload-failed" && (
                <button
                  type="button"
                  onClick={retryUpload}
                  style={{
                    border: "1px solid rgba(255,255,255,0.4)",
                    background: "transparent",
                    color: "#fff",
                    borderRadius: "0.25rem",
                    padding: "0.2rem 0.45rem",
                    cursor: "pointer"
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {recordingError && (
            <p style={{ color: "#fecaca", margin: 0, fontSize: "0.78rem" }}>
              {recordingError}
            </p>
          )}
          {proctoringPersistError && (
            <p style={{ color: "#fde68a", margin: 0, fontSize: "0.78rem" }}>
              {proctoringPersistError}
            </p>
          )}
          
          {/* Progress indicator */}
          {interviewStarted && (
            <div
              style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              maxWidth: "600px",
              gap: "1rem",
              flexWrap: "wrap"
            }}
            >
              {/* Center progress */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  justifyContent: "center",
                  flex: "1 1 auto",
                  minWidth: "200px"
                }}
              >
                <span>
                  Question {Math.min(currentIndex, totalQuestions)} of {totalQuestions}
                </span>

                <div
                  style={{
                    width: "9.375rem",
                    height: "0.375rem",
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: "0.1875rem",
                    overflow: "hidden"
                  }}
                >
                  <div
                    style={{
                      width: `${(Math.min(currentIndex, totalQuestions) / totalQuestions) * 100}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #0a9e05ff, #46f772ff)",
                      transition: "width 0.3s ease"
                    }}
                  />
                </div>
              </div>

              {/* Right-aligned Stop Button */}
              <button
                onClick={async () => {
                  // Immediately update UI (no lag)
                  isStoppedRef.current = true;
                  setWaitingForUser(false);
                  setInterviewStarted(false);  // Hide button immediately
                  setShowInterruptionButtons(true);  // Show panel immediately
                  
                  // ⚡ Cancel any pending API requests (only for getNextQuestion)
                  if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                    console.log("⏹️ Aborted pending API requests");
                  }
                  
                  // Stop audio/recognition
                  if (audioRef.current) audioRef.current.pause();
                  sttStop();
                  flushProctoring();
                  stopRecording();
                  // Mark as submitted to prevent any restarts
                  hasSubmittedRef.current = true;
                  if (noSpeechTimerRef.current) {
                    clearInterval(noSpeechTimerRef.current);
                    noSpeechTimerRef.current = null;
                  }
                  if (userResponseTimeoutRef.current) clearTimeout(userResponseTimeoutRef.current);
                  userResponseTimeoutRef.current = null;
                  if (rafRef.current) cancelAnimationFrame(rafRef.current);
                  
                  // ✅ CALL BACKEND stop_interview FIRST (triggers evaluation)
                  try {
                    const sessionId = sessionIdRef.current || sessionStorage.getItem('mockbot_session_id');
                    if (sessionId) {
                      const idToken = await getAuthToken();
                      if (idToken) {
                        console.log("🛑 Stopping interview session:", sessionId);
                        await fetch(`${apiBase}/stop_interview`, {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${idToken}`
                          },
                          body: JSON.stringify({ session_id: sessionId })
                        });
                        console.log("✅ Interview stopped, evaluation scheduled");
                        onInterviewStopped?.({ sessionId });
                      }
                    }
                  } catch (error) {
                    console.error("❌ Error stopping interview:", error);
                  }
                  
                  // Play interruption audio + lip sync.
                  // Trial: show paywall as soon as audio starts (onPlay).
                  // Non-trial: default behaviour (show download buttons on end).
                  const onAudioPlay = isTrial
                    ? () => {
                        setStatus("Free trial completed");
                        setInterviewStarted(false);
                        setShowTrialModal(true);
                      }
                    : null;
                  getInterruptionMessage(onAudioPlay, null);
                }}
                className="logout-button"
                style={{
                  padding: "0.375rem 0.875rem",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap"
                }}
              >
                ⏹ Stop Interview
              </button>
            </div>
          )}
          
          
        </div>
      </div>

      {/* ---------------- CONVERSATION PANEL ---------------- */}
      <div className="conversation-container">
        <h3 className="conversation-header">Conversation</h3>

        <div className="messages-container">
          {conversation.map((msg, i) => (
            <div key={i} className={`message ${msg.speaker}`}>
              <div className="message-bubble">{msg.text}</div>
            </div>
          ))}
          
          {/* User turn bubble — "Listening…" while recording, final text after stop */}
          {waitingForUser && (
            <div className="message user" style={{ opacity: 0.85 }}>
              <div className="message-bubble" style={{
                borderStyle: "dashed",
                background: "rgba(102, 126, 234, 0.3)",
                position: "relative",
                minHeight: "1.5rem",
              }}>
                {sttLiveText ? (
                  <span>{sttLiveText}</span>
                ) : (
                  <span style={{ opacity: 0.5, fontStyle: "italic" }}>Listening…</span>
                )}
                {isListening && (
                  <span style={{
                    marginLeft: "0.5rem",
                    animation: "blink 1s infinite",
                    fontSize: "1.125rem",
                    color: "#4CAF50",
                  }}>●</span>
                )}
              </div>
            </div>
          )}
          
          {/* Invisible element for auto-scroll */}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}




